import { jsPDF } from "jspdf";
import {
  TEXT_X_MM, TEXT_WIDTH_MM,
  mmToPt, drawHeader, drawFooter, ensureSpace, getTextRegion, getSignatureLineY,
} from "./invoiceLayout";
import { fmtDatePdf, fmtINR } from "@emr/shared/india";

export interface InvoiceLineItem {
  serviceId?: string | null;
  name: string;
  quantity: number;
  amountPaise: number;
}

export interface InvoicePdfData {
  number: string;
  date: string;
  patientName: string;
  clinicUhid: string | null;
  patientPhone: string | null;
  lineItems: InvoiceLineItem[];
  subtotalPaise: number;
  totalPaise: number;
  paidPaise: number;
  status: string;
  doctorName: string | null;
  doctorRegNumber: string | null;
}

export async function createInvoicePdf(data: InvoicePdfData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  // Invoice # + date.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(124, 45, 18);
  doc.text(`Invoice ${data.number}`, x, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 95, 88);
  doc.text(fmtDatePdf(data.date), x + w, y, { align: "right" });
  y += mmToPt(7);

  // Patient info.
  doc.setTextColor(26, 20, 19);
  doc.setFontSize(10);
  doc.text(`Patient: ${data.patientName}`, x, y);
  if (data.clinicUhid) {
    doc.text(`UHID: ${data.clinicUhid}`, x + w, y, { align: "right" });
  }
  y += mmToPt(5);
  if (data.patientPhone) {
    doc.text(`Phone: ${data.patientPhone}`, x, y);
  }
  doc.text(`Status: ${data.status}`, x + w, y, { align: "right" });
  y += mmToPt(6);

  // Line items table.
  const colSr = mmToPt(10);
  const colQty = mmToPt(20);
  const colAmt = mmToPt(30);
  const colName = w - colSr - colQty - colAmt;

  doc.setFillColor(247, 240, 232);
  doc.rect(x, y, w, mmToPt(7), "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(124, 45, 18);
  let cx = x + mmToPt(2);
  doc.text("Sr #", cx, y + mmToPt(5));
  cx += colSr;
  doc.text("Service", cx, y + mmToPt(5));
  cx += colName;
  doc.text("Qty", cx, y + mmToPt(5));
  cx += colQty;
  doc.text("Amount", cx, y + mmToPt(5));
  y += mmToPt(7);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(26, 20, 19);
  doc.setFontSize(10);

  let i = 1;
  for (const item of data.lineItems) {
    const lines = doc.splitTextToSize(item.name, colName - mmToPt(3));
    const rowH = mmToPt(6) * lines.length;
    y = await ensureSpace(doc, y, rowH);
    let cx2 = x + mmToPt(2);
    doc.text(String(i), cx2, y + mmToPt(4));
    cx2 += colSr;
    doc.text(lines, cx2, y + mmToPt(4));
    cx2 += colName;
    doc.text(String(item.quantity), cx2, y + mmToPt(4));
    cx2 += colQty;
    doc.text(fmtINR(item.amountPaise), cx2 + colAmt - mmToPt(2), y + mmToPt(4), { align: "right" });
    y += rowH;
    i++;
  }

  y += mmToPt(4);

  // Totals.
  doc.setDrawColor(200, 190, 180);
  doc.line(x, y, x + w, y);
  y += mmToPt(5);
  doc.setFont("helvetica", "normal");
  doc.text("Subtotal", x + w - mmToPt(60), y);
  doc.text(fmtINR(data.subtotalPaise), x + w, y, { align: "right" });
  y += mmToPt(5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Total", x + w - mmToPt(60), y);
  doc.text(fmtINR(data.totalPaise), x + w, y, { align: "right" });
  y += mmToPt(5);
  if (data.paidPaise > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Paid", x + w - mmToPt(60), y);
    doc.text(fmtINR(data.paidPaise), x + w, y, { align: "right" });
    y += mmToPt(5);
    doc.text("Balance due", x + w - mmToPt(60), y);
    doc.text(fmtINR(data.totalPaise - data.paidPaise), x + w, y, { align: "right" });
    // No trailing y increment — the signatory block below anchors to the
    // footer (getSignatureLineY), never to y.
  }

  // Signatory block (anchored above the footer).
  const signY = getSignatureLineY(doc);
  const signX = x + w - mmToPt(60);
  doc.setDrawColor(200, 190, 180);
  doc.line(signX, signY, signX + mmToPt(50), signY);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(data.doctorName ?? "Authorized signatory", signX + mmToPt(50), signY + mmToPt(4), { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 95, 88);
  if (data.doctorRegNumber) {
    doc.text(`Reg No: ${data.doctorRegNumber}`, signX + mmToPt(50), signY + mmToPt(7), { align: "right" });
  }

  return doc;
}
